// Centralizovaná data o oblasti působnosti pro SEO a konzistenci

export interface City {
  name: string;
  coords?: [number, number]; // [lat, lng]
  distance?: number; // vzdálenost od Teplic v km
}

export interface SecondaryRegion {
  name: string;
  note: string;
  cities: City[];
}

export const SERVICE_AREA = {
  center: {
    name: "Teplice",
    coords: [50.6404, 13.8244] as [number, number],
  },
  radius: 50, // km
  region: "Ústecký kraj",
  
  // Okresní města - prioritní pro SEO
  mainCities: [
    { name: "Ústí nad Labem", coords: [50.6610, 14.0324] as [number, number], distance: 15 },
    { name: "Most", coords: [50.5031, 13.6366] as [number, number], distance: 18 },
    { name: "Litoměřice", coords: [50.5336, 14.1318] as [number, number], distance: 25 },
    { name: "Chomutov", coords: [50.4603, 13.4180] as [number, number], distance: 35 },
    { name: "Děčín", coords: [50.7739, 14.2141] as [number, number], distance: 40 },
  ] as City[],
  
  // Větší města v okolí
  secondaryCities: [
    { name: "Bílina", coords: [50.5481, 13.7757] as [number, number], distance: 10 },
    { name: "Duchcov", coords: [50.6032, 13.7459] as [number, number], distance: 8 },
    { name: "Louny", coords: [50.3569, 13.7965] as [number, number], distance: 30 },
    { name: "Kadaň", coords: [50.3777, 13.2715] as [number, number], distance: 45 },
    { name: "Lovosice", coords: [50.5151, 14.0512] as [number, number], distance: 22 },
    { name: "Roudnice nad Labem", coords: [50.4252, 14.2618] as [number, number], distance: 35 },
  ] as City[],
  
  // Menší města a obce
  otherCities: [
    { name: "Krupka", coords: [50.6847, 13.8579] as [number, number], distance: 6 },
    { name: "Osek", coords: [50.6234, 13.6916] as [number, number], distance: 12 },
    { name: "Dubí", coords: [50.6862, 13.7873] as [number, number], distance: 7 },
    { name: "Chabařovice", coords: [50.6741, 13.9386] as [number, number], distance: 10 },
    { name: "Jirkov", coords: [50.4996, 13.4476] as [number, number], distance: 32 },
    { name: "Klášterec nad Ohří", coords: [50.3861, 13.1713] as [number, number], distance: 48 },
    { name: "Žatec", coords: [50.3273, 13.5463] as [number, number], distance: 42 },
    { name: "Litvínov", coords: [50.6009, 13.6113] as [number, number], distance: 18 },
  ] as City[],

  // SEKUNDÁRNÍ OBLAST: Praha a Středočeský kraj
  secondaryRegion: {
    name: "Praha a Středočeský kraj",
    note: "Pro větší projekty a firemní zakázky",
    cities: [
      { name: "Praha", coords: [50.0755, 14.4378] as [number, number] },
      { name: "Kladno", coords: [50.1477, 14.1050] as [number, number] },
      { name: "Mělník", coords: [50.3505, 14.4747] as [number, number] },
      { name: "Neratovice", coords: [50.2592, 14.5175] as [number, number] },
      { name: "Brandýs nad Labem", coords: [50.1867, 14.6600] as [number, number] },
      { name: "Kralupy nad Vltavou", coords: [50.2411, 14.3114] as [number, number] },
    ] as City[],
  } as SecondaryRegion,
} as const;

// Helper funkce pro získání všech měst primární oblasti
export const getAllCities = (): City[] => [
  SERVICE_AREA.center,
  ...SERVICE_AREA.mainCities,
  ...SERVICE_AREA.secondaryCities,
  ...SERVICE_AREA.otherCities,
];

// Helper pro získání měst pro mapu (s koordináty)
export const getCitiesForMap = (): City[] => [
  ...SERVICE_AREA.mainCities,
  ...SERVICE_AREA.secondaryCities.slice(0, 4), // Top 4 sekundárních měst
];

// SEO helper - seznam měst pro meta description
export const getCitiesForSEO = (): string => {
  const cities = [
    SERVICE_AREA.center.name,
    ...SERVICE_AREA.mainCities.map(c => c.name),
    ...SERVICE_AREA.secondaryCities.slice(0, 3).map(c => c.name),
  ];
  return cities.join(", ");
};

// SEO helper - schema.org areaServed array (včetně Prahy)
export const getAreaServedSchema = () => [
  // Primární oblast - Ústecký kraj
  {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": SERVICE_AREA.center.coords[0],
      "longitude": SERVICE_AREA.center.coords[1],
    },
    "geoRadius": `${SERVICE_AREA.radius * 1000}`,
  },
  {
    "@type": "AdministrativeArea",
    "name": SERVICE_AREA.region,
  },
  {
    "@type": "City",
    "name": SERVICE_AREA.center.name,
  },
  ...SERVICE_AREA.mainCities.map(city => ({
    "@type": "City",
    "name": city.name,
  })),
  ...SERVICE_AREA.secondaryCities.map(city => ({
    "@type": "City",
    "name": city.name,
  })),
  // Sekundární oblast - Praha a Středočeský kraj
  {
    "@type": "AdministrativeArea",
    "name": "Středočeský kraj",
  },
  ...SERVICE_AREA.secondaryRegion.cities.map(city => ({
    "@type": "City",
    "name": city.name,
  })),
];

// Helper pro generování SEO meta description s městy pro konkrétní službu
export const getServiceMetaDescription = (serviceName: string): string => {
  const primaryCities = [
    SERVICE_AREA.center.name,
    ...SERVICE_AREA.mainCities.slice(0, 3).map(c => c.name),
  ].join(", ");
  
  return `${serviceName} ${primaryCities} - ESKO Servis. Profesionální práce, férové ceny. Působíme i v Praze a okolí.`;
};

// Helper pro získání kompaktního seznamu měst pro textový obsah
export const getCitiesTextList = (): string => {
  const cities = [
    SERVICE_AREA.center.name,
    ...SERVICE_AREA.mainCities.map(c => c.name),
  ];
  return cities.join(", ");
};

// Helper pro získání všech měst primární i sekundární oblasti
export const getAllAreaCities = (): string[] => [
  SERVICE_AREA.center.name,
  ...SERVICE_AREA.mainCities.map(c => c.name),
  ...SERVICE_AREA.secondaryCities.map(c => c.name),
  ...SERVICE_AREA.secondaryRegion.cities.map(c => c.name),
];
